import React, { useEffect } from "react";
import "./TermsConditionsPage.css";
import { useLocation } from "react-router-dom";
import SEO from "./SEO";

const TermsConditionsPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <>
      <SEO
        title="Terms and Conditions | Waggex HRMS & Payroll Software"
        description="Read Waggex terms and conditions for using our HRMS, payroll, attendance, and employee management software services in India."
        canonical="https://www.waggex.com/terms-conditions"
      />
      <section id="CookiespolicyPage">
        <div className="container">
          <div className="cookies-policy-container">
            <h1 className="text-center">Terms & Conditions</h1>
            {/* Section 1: What Are Cookies? */}
            <p>
              <strong>Effective Date:</strong> October 30, 2025
              <br />
              <strong>Last Updated:</strong> October 30, 2025
            </p>
            <h2>1. Introduction</h2>
            <p>
              Welcome to Waggex, a cloud-based Human Resource Management System
              (HRMS) that provides payroll, attendance, employee management,
              leave management, recruitment, expense management, and workforce
              automation solutions.
            </p>
            <p>
              Waggex is owned, operated, and managed by Digital Engineering
              Excellence Programming ("DEEP"). These Terms of Service ("Terms")
              govern your access to and use of the Waggex website, applications,
              software, and related services (collectively referred to as the
              "Services").
            </p>
            <p>
              By accessing or using the Services, you agree to comply with and
              be legally bound by these Terms. If you do not agree with any part
              of these Terms, you must not access or use the Services.
            </p>
            <p>
              <strong>Legal Entity:</strong> Digital Engineering Excellence
              Programming ("DEEP")
              <br />
              <strong>Brand Name: Waggex</strong>
            </p>
            <p>
              Throughout these Terms, the terms "Waggex," "we," "our," and "us"
              refer to Digital Engineering Excellence Programming, the owner and
              operator of the Waggex platform
            </p>
            {/* Section 2: How Waggex Uses Cookies */}
            <h2>2. Account Registration and Eligibility</h2>
            <h6>2.1. Eligibility:</h6>
            <p>
              The Services are intended for businesses, organizations,
              institutions, and professional entities. By creating an account,
              you confirm that:
            </p>
            <ul>
              <li>You are at least 18 years of age.</li>
              <li>
                You have the legal authority to enter into this agreement.
              </li>
              <li>
                You are authorized to act on behalf of the organization
                registering for the Services.
              </li>
            </ul>
            <h6>2.2. Account Information:</h6>
            <p>
              You agree to provide accurate, complete, and up-to-date
              information while creating your account. You are responsible for
              maintaining the confidentiality of your login credentials and for
              all activities that occur under your account.
            </p>
            <h6>2.3. Compliance:</h6>
            <p>
              You agree to use the Services in compliance with all applicable
              local, state, national, and international laws and regulations.
            </p>
            <h2>3. Our Services</h2>
            <p>
              Digital Engineering Excellence Programming, through its Waggex
              platform, provides cloud-based HR and workforce management
              solutions, including but not limited to:
            </p>
            <ul>
              <li>Payroll Management</li>
              <li>Attendance Management</li>
              <li>Employee Management</li>
              <li>Leave Management</li>
              <li>Recruitment & Hiring</li>
              <li>Shift & Roster Management</li>
              <li>Expense & Reimbursement Management</li>
              <li>Performance Management</li>
              <li>Employee Self-Service Portal</li>
              <li>Reports & Analytics</li>
              <li>Payslip Generation</li>
              <li>HR Automation Tools</li>
            </ul>
            <p>
              <strong>Service Accuracy:</strong> While we strive to provide
              accurate calculations and reliable services, the accuracy of
              payroll, attendance, reports, and other outputs depends on the
              information submitted by you. We are not responsible for errors
              caused by inaccurate, incomplete, or incorrect information
              provided by users.
            </p>
            <h2>4. Subscription, Payments and Fees</h2>
            <h6>4.1. Subscription:</h6>
            <p>
              Access to certain features of the Services requires a paid
              subscription. Subscription plans may be billed monthly, quarterly,
              annually, or according to the plan selected during registration.
            </p>
            <h6>4.2. Fee Changes:</h6>
            <p>
              Digital Engineering Excellence Programming reserves the right to
              modify subscription fees at any time. Any pricing changes will
              become effective after providing reasonable prior notice and will
              apply to future billing cycles.
            </p>
            <h6>4.3. Payment:</h6>
            <p>
              You agree to provide valid and current payment information.
              Failure to make timely payments may result in suspension or
              termination of your account.
            </p>
            <h6>4.4. Refund Policy :</h6>
            <p>
              Unless otherwise stated in writing, subscription fees are
              non-refundable.
            </p>
            <h2>5. Account Suspension and Termination</h2>
            <h6>5.1. Suspension by Us:</h6>
            <p>
              Digital Engineering Excellence Programming reserves the right to
              suspend or terminate any account that:
            </p>
            <ul>
              <li>Violates these Terms.</li>
              <li>Uses the Services unlawfully.</li>
              <li>Fails to pay subscription fees.</li>
              <li>Engages in fraudulent or abusive activities.</li>
            </ul>
            <h6>5.2. Termination by You:</h6>
            <p>
              You may cancel your subscription at any time. Your access will
              remain active until the end of your current billing period unless
              otherwise specified.
            </p>
            <h2>5.3. Data Retention:</h2>
            <p>
              Following account termination, we may retain your data for up to
              90 days for legal, compliance, backup, or account restoration
              purposes. After this period, your data may be permanently deleted.
            </p>
            <h2>6. Intellectual Property</h2>
            <p>
              The <strong>Waggex</strong> software, website, mobile
              applications, user interface, design, source code, logos,
              trademarks, content, documentation, and all related intellectual
              property are owned exclusively by{" "}
              <strong>Digital Engineering Excellence Programming. </strong>
            </p>
            <p>
              <strong>Waggex</strong> is a registered trademark of Digital
              Engineering Excellence Programming.
            </p>
            <p>
              No part of the Services may be copied, reproduced, distributed,
              modified, reverse engineered, or commercially exploited without
              prior written permission.
            </p>
            <h2>7. User Responsibilities</h2>
            <p>You agree that you will not:</p>
            <ul>
              <li>Use the Services for any unlawful purpose.</li>{" "}
              <li>Upload malicious software or harmful code.</li>{" "}
              <li>Attempt unauthorized access to our systems.</li>{" "}
              <li>
                Use another organization's identity or information fraudulently.
              </li>{" "}
              <li>Misrepresent payroll, attendance, or employee records.</li>{" "}
              <li>
                Violate applicable employment, tax, or data protection laws.
              </li>
            </ul>
            <h2>8. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law,{" "}
              <strong>Digital Engineering Excellence Programming</strong>,
              including its directors, employees, affiliates, contractors,
              licensors, and partners, shall not be liable for any indirect,
              incidental, consequential, special, exemplary, or punitive damages
              arising from:
            </p>
            <ul>
              <li>
                Your use of the Services. Errors caused by inaccurate
                user-provided data. Service interruptions.
              </li>
              <li> Unauthorized access to your account.</li>
              <li> Loss of profits, business, goodwill, or data.</li>
            </ul>
            <p>
              Our total liability shall not exceed the amount paid by you for
              the Services during the preceding twelve (12) months.
            </p>
            <h2>9. Privacy and Data Protection</h2>
            <p>
              Your use of the Services is also governed by our Privacy Policy.
            </p>
            <p>
              We implement commercially reasonable security measures to protect
              your information. However, no online system is completely secure,
              and we cannot guarantee absolute security.
            </p>
            <h2>10. Disclaimer Regarding Payslip Generation</h2>
            <section className="disclaimer">
              <p>
                Waggex provides tools for generating professional payroll
                documents and payslips.{" "}
              </p>
              <p>
                Users are solely responsible for the information entered into
                the platform.
              </p>
              <p>
                {" "}
                Creating fake, misleading, forged, or fraudulent payslips using
                another organization's name, logo, employee information, or
                other identifying details is strictly prohibited.{" "}
              </p>
              <p>
                <strong>Digital Engineering Excellence Programming</strong> acts
                solely as a software provider and assumes no responsibility for
                any misuse of the platform by users.
              </p>
              <p>
                {" "}
                Any person engaging in fraudulent activities shall be solely
                responsible under applicable laws.
              </p>
            </section>

            <p></p>
            <h2>11. Governing Law and Jurisdiction</h2>
            <p>
              These Terms shall be governed by and interpreted in accordance
              with the laws of the Republic of India
            </p>
            <p>
              Any disputes arising from these Terms or the Services shall be
              subject to the exclusive jurisdiction of the competent courts
              located in{" "}
              <strong>
                Sahibzada Ajit Singh Nagar (Mohali), Punjab, India
              </strong>
            </p>
            <h2>12.Changes to These Terms</h2>
            <p>
              We reserve the right to update or modify these Terms at any time.
            </p>
            <p>
              Any material changes will be communicated through the website or
              via email where appropriate. Continued use of the Services after
              such changes constitutes acceptance of the revised Terms.
            </p>
            <h2>13.Brand Ownership</h2>
            <p>
              <strong>Waggex</strong> is a registered trademark and software
              product owned and operated by{" "}
              <strong>Digital Engineering Excellence Programming</strong>.
            </p>
            <p>
              All references to <strong>"Waggex"</strong> throughout these Terms
              refer to the software platform and services provided by{" "}
              <strong>Digital Engineering Excellence Programming.</strong>
            </p>
            <h2>14. Contact Us</h2>
            <p>
              If you have any questions regarding these Terms, please contact
              us:
            </p>
            <p>
              <strong>
                {" "}
                Digital Engineering Excellence Programming (DEEP)
              </strong>{" "}
            </p>
            <p>
              <strong> Operating Brand: Waggex</strong>
            </p>
            <p>
              <strong>Address:</strong> Phase 8-B, Industrial Area, Sector 74,
              Sahibzada Ajit Singh Nagar, Punjab – 140307, India
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:support@waggex.com" rel="canonical">
                support@waggex.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsConditionsPage;
